/**
 * Reproduce una hoja de un formulario oficial hecho en cuadrícula (celdas combinadas, bordes, rellenos) a partir de la plantilla del original:
 * `datos/form033.json` sale del Excel oficial del 033 (columna y fila exactas, mismos rótulos, mismas casillas). Encima se escriben los datos del
 * paciente en la celda que corresponde a cada campo. Todo se mide en porcentaje del ancho, así que la hoja se ve igual en pantalla y en el papel A4.
 */
import type { CSSProperties, ReactNode } from "react";
import datos from "../datos/form033.json";
import datos024 from "../datos/form024.json";

export type NombreHoja = "h1" | "h2" | "a024" | "b024";
/** fila, columna, filas, columnas, texto, bordes (arriba izquierda abajo derecha), relleno, tamaño de letra (pt), 1 negrita + 2 cursiva, alineación, alineación vertical, ajusta línea, giro */
type Celda = [number, number, number, number, string, string, string, number, number, string, string, number, number];
interface DatosHoja { w: number[]; h: number[]; c: Celda[] }
const PLANTILLA: Record<NombreHoja, DatosHoja> = { h1: (datos as unknown as Record<string, DatosHoja>).h1, h2: (datos as unknown as Record<string, DatosHoja>).h2, a024: (datos024 as unknown as Record<string, DatosHoja>).h1, b024: (datos024 as unknown as Record<string, DatosHoja>).h2 };

export function columnaDe(s: string) { let n = 0; for (const ch of s) n = n * 26 + ch.charCodeAt(0) - 64; return n; }
/** «AB12» → columna 28, fila 12. */
export function celdaDe(ref: string): { r: number; c: number } {
  const m = /^([A-Z]+)(\d+)$/.exec(ref);
  if (!m) throw new Error(`Referencia de celda no válida: ${ref}`);
  return { r: Number(m[2]), c: columnaDe(m[1]) };
}

const GROSOR: Record<string, number> = { "1": 1.1, "2": 2.2, "3": 3.2, "4": 1, "5": 1, "6": 3 };
const PESO: Record<string, number> = { "1": 1, "2": 2, "3": 3, "4": 0.5, "5": 0.7, "6": 3 };
interface Linea { x: number; y: number; w: number; h: number; k: string }
interface Geometria { cx: number[]; ry: number[]; W: number; Ht: number; lineas: Linea[]; porCelda: Map<string, Celda>; rellenos: Celda[]; textos: Celda[] }
const cache: Partial<Record<NombreHoja, Geometria>> = {};

function geometria(nombre: NombreHoja): Geometria {
  const hecha = cache[nombre];
  if (hecha) return hecha;
  const P = PLANTILLA[nombre];
  const cx = [0]; P.w.forEach((x) => cx.push(cx.at(-1)! + x));
  const ry = [0]; P.h.forEach((x) => ry.push(ry.at(-1)! + x));
  const H = new Map<string, string>(), V = new Map<string, string>();
  const poner = (m: Map<string, string>, k: string, v: string) => { const a = m.get(k); if (!a || (PESO[v] ?? 1) > (PESO[a] ?? 1)) m.set(k, v); };
  const porCelda = new Map<string, Celda>();
  const celdas: Celda[] = P.c.map((c) => {
    // el original trae un 83 donde va el 82 (temporal inferior derecho) y «20XX» en el pie
    if (nombre === "h1" && c[0] === 56 && c[1] === 20 && c[4] === "83") c = [...c] as Celda, c[4] = "82";
    // los TOTAL de CPO y ceo traen un «0» de la fórmula del Excel: se borra para que no se encime al valor real
    if (nombre === "h1" && c[1] === 61 && (c[0] === 68 || c[0] === 72) && c[4] === "0") { c = [...c] as Celda; c[4] = ""; }
    if (/20XX/.test(c[4])) { c = [...c] as Celda; c[4] = c[4].replace("20XX", "2021"); }
    // los rótulos «X rojo» y «X azul» de la simbología son el texto de los símbolos que se dibujan encima
    if (nombre === "h1" && c[1] === 26 && c[0] >= 75 && c[0] <= 84 && c[4]) { c = [...c] as Celda; c[4] = ""; }
    return c;
  });
  for (const c of celdas) {
    const [r, col, rs, cs, , bd] = c;
    porCelda.set(`${r},${col}`, c);
    for (let k = 0; k < cs; k++) { if (bd[0] !== "0") poner(H, `${r},${col + k}`, bd[0]); if (bd[2] !== "0") poner(H, `${r + rs},${col + k}`, bd[2]); }
    for (let k = 0; k < rs; k++) { if (bd[1] !== "0") poner(V, `${r + k},${col}`, bd[1]); if (bd[3] !== "0") poner(V, `${r + k},${col + cs}`, bd[3]); }
  }
  const lineas: Linea[] = [];
  const nf = P.h.length, nc = P.w.length;
  for (let r = 1; r <= nf + 1; r++) {
    let c = 1;
    while (c <= nc) {
      const k = H.get(`${r},${c}`);
      if (!k) { c++; continue; }
      let c2 = c; while (c2 + 1 <= nc && H.get(`${r},${c2 + 1}`) === k) c2++;
      lineas.push({ x: cx[c - 1], y: ry[r - 1] - GROSOR[k] / 2, w: cx[c2] - cx[c - 1], h: GROSOR[k], k }); c = c2 + 1;
    }
  }
  for (let c = 1; c <= nc + 1; c++) {
    let r = 1;
    while (r <= nf) {
      const k = V.get(`${r},${c}`);
      if (!k) { r++; continue; }
      let r2 = r; while (r2 + 1 <= nf && V.get(`${r2 + 1},${c}`) === k) r2++;
      lineas.push({ x: cx[c - 1] - GROSOR[k] / 2, y: ry[r - 1], w: GROSOR[k], h: ry[r2] - ry[r - 1], k }); r = r2 + 1;
    }
  }
  const g = { cx, ry, W: cx.at(-1)!, Ht: ry.at(-1)!, lineas, porCelda, rellenos: celdas.filter((c) => c[6]), textos: celdas.filter((c) => c[4]) };
  cache[nombre] = g;
  return g;
}

/** Anchos de las hojas (en unidades de la plantilla), para fijar la proporción en pantalla y en papel. */
export function medidasHoja(nombre: NombreHoja) { const g = geometria(nombre); return { ancho: g.W, alto: g.Ht }; }

/** Lo que se escribe sobre una celda (o un bloque de celdas) de la plantilla. */
export interface Relleno {
  /** celda de arriba a la izquierda, p. ej. «A3» */
  ref: string;
  /** cuántas filas y columnas ocupa; si no se dice, las de la celda combinada de la plantilla */
  filas?: number; columnas?: number;
  texto?: ReactNode;
  /** casilla marcada */
  x?: boolean;
  /** tamaño de letra en pt (por defecto 9) */
  pt?: number;
  alinear?: "l" | "c" | "r";
  negrita?: boolean;
  /** varias líneas de texto, una por renglón de la plantilla (la letra se achica si el texto es largo) */
  renglones?: boolean;
  /** varias líneas dentro de la celda, sin renglones (letra chica) */
  ajustar?: boolean;
  /** cuántas líneas de texto caben en cada renglón de la plantilla (1 por defecto) */
  porRenglon?: number;
  /** dibujo o contenido libre que llena la celda */
  hijo?: ReactNode;
  vertical?: "t" | "m";
}

const CSS_BORDE: Record<string, string> = { "4": "dotted", "5": "dashed", "6": "double" };

export function PlantillaHoja({ nombre, rellenos, etiqueta }: { nombre: NombreHoja; rellenos: Relleno[]; etiqueta: string }) {
  const g = geometria(nombre);
  const { cx, ry, W, Ht } = g;
  const px = (v: number) => `${((v / W) * 100).toFixed(4)}%`, py = (v: number) => `${((v / Ht) * 100).toFixed(4)}%`;
  const caja = (r: number, c: number, rs: number, cs: number): CSSProperties => ({ left: px(cx[c - 1]), top: py(ry[r - 1]), width: px(cx[c - 1 + cs] - cx[c - 1]), height: py(ry[r - 1 + rs] - ry[r - 1]) });
  const fuente = (pt: number) => `calc(var(--cq) * ${(pt * 96 / 72).toFixed(2)})`;
  return (
    <div className="of-xw" style={{ ["--cq" as string]: `${100 / W}cqw`, ["--papel" as string]: `${Math.min(182, Math.floor(244 / (Ht / W)))}mm` }}>
      <div className="of-x" role="group" aria-label={etiqueta} style={{ aspectRatio: `${W} / ${Ht}` }}>
        {g.rellenos.map((c) => <div key={`f${c[0]},${c[1]}`} className="of-x-fondo" style={{ ...caja(c[0], c[1], c[2], c[3]), background: c[6] }} />)}
        {g.lineas.map((l, i) => (
          <div key={i} className="of-x-linea" style={{ left: px(l.x), top: py(l.y), width: px(l.w), height: py(l.h), ...(CSS_BORDE[l.k] ? { background: "none", [l.w > l.h ? "borderTop" : "borderLeft"]: `calc(var(--cq) * ${GROSOR[l.k]}) ${CSS_BORDE[l.k]} #000` } : null) }} />
        ))}
        {g.textos.map((c) => {
          const alin = c[9] === "c" ? "center" : c[9] === "r" ? "flex-end" : "flex-start";
          const vert = c[10] === "t" ? "flex-start" : c[10] === "b" ? "flex-end" : "center";
          return (
            <div key={`t${c[0]},${c[1]}`} className="of-x-texto" style={{ ...caja(c[0], c[1], c[2], c[3]), fontSize: fuente(c[7]), fontWeight: c[8] & 1 ? 700 : 400, fontStyle: c[8] & 2 ? "italic" : "normal",
              justifyContent: alin, alignItems: vert, textAlign: c[9] === "c" ? "center" : c[9] === "r" ? "right" : "left", whiteSpace: c[11] ? "normal" : "nowrap",
              ...(c[12] === 90 ? { writingMode: "vertical-rl", transform: "rotate(180deg)" } : c[12] === 255 ? { writingMode: "vertical-rl" } : null) }}>
              <span>{c[4]}</span>
            </div>
          );
        })}
        {rellenos.map((x) => {
          const { r, c } = celdaDe(x.ref);
          const base = g.porCelda.get(`${r},${c}`);
          const rs = x.filas ?? base?.[2] ?? 1, cs = x.columnas ?? base?.[3] ?? 1;
          const alto = ry[r - 1 + rs] - ry[r - 1];
          const al = x.alinear ?? "l";
          const pt = x.pt ?? 9;
          const renglon = x.renglones ? (ry[r] - ry[r - 1]) : 0;
          const estilo: CSSProperties = { ...caja(r, c, rs, cs), fontSize: fuente(x.x ? 11 : pt), fontWeight: x.negrita || x.x ? 700 : 400,
            justifyContent: al === "c" ? "center" : al === "r" ? "flex-end" : "flex-start", alignItems: x.vertical === "t" || x.renglones ? "flex-start" : "center",
            textAlign: al === "c" ? "center" : al === "r" ? "right" : "left", whiteSpace: x.renglones ? "pre-wrap" : x.ajustar ? "normal" : "nowrap", ...(x.ajustar ? { lineHeight: 1.05 } : null),
            ...(x.renglones ? { lineHeight: `calc(var(--cq) * ${(renglon / (x.porRenglon ?? 1)).toFixed(2)})` } : null) };
          void alto;
          return <div key={`${x.ref}-${r}-${c}`} className={`of-x-dato${x.hijo ? " hijo" : ""}`} style={estilo}>{x.hijo ?? (x.x ? "X" : x.texto)}</div>;
        })}
      </div>
    </div>
  );
}

/** Ancho en unidades de la plantilla de un bloque de columnas que empieza en la celda `ref`. */
export function anchoDe(nombre: NombreHoja, ref: string, columnas: number): number {
  const { c } = celdaDe(ref); const g = geometria(nombre);
  return g.cx[c - 1 + columnas] - g.cx[c - 1];
}

/** Letra y líneas por renglón según el largo del texto: cabe en los renglones de la plantilla o se achica; `max` es lo más que cabe. */
export function ajusteTexto(texto: string, anchoPx: number, renglones: number): { pt: number; por: number; max: number } {
  const cabe = (pt: number, por: number) => Math.floor((anchoPx / (pt * 96 / 72 * 0.52)) * renglones * por * 0.85);
  for (const [pt, por] of [[8, 1], [7, 2], [6.5, 3]] as const) if (texto.length <= cabe(pt, por)) return { pt, por, max: cabe(pt, por) };
  return { pt: 6.5, por: 3, max: cabe(6.5, 3) };
}
/** Parte un texto largo en trozos de hasta `max` letras, cortando en espacios. */
export function partirTexto(texto: string, max: number): string[] {
  if (texto.length <= max) return [texto];
  const trozos: string[] = []; let actual = "";
  for (const palabra of texto.split(/(\s+)/)) {
    if ((actual + palabra).length > max && actual.trim()) { trozos.push(actual.trim()); actual = ""; }
    actual += palabra;
  }
  if (actual.trim()) trozos.push(actual.trim());
  return trozos;
}
